import {css, StyleSheet} from 'aphrodite';
import React, {useMemo, useState} from 'react';

import {assertAndContinue} from 'Asserts';
import {ProcessFlowchartNodeDetailsType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {FlowchartBaseNode} from 'processes/graph-builder/flowchart-viz/FlowchartVizConstants';
import {
  GraphBuilderSidebar,
  GraphBuilderSidebarSide,
} from 'processes/graph-builder/flowchart-viz/GraphBuilderSidebar';
import {
  ProcessFlowchartInteractiveNode,
  ProcessFlowchartNodeWidth,
} from 'processes/graph-builder-processes/flowchart-viz/ProcessFlowchartVizConstants';
import {
  END_NODE_INFO,
  ROUTER_NODE_INFO,
  TASK_NODE_INFO,
} from 'processes/graph-builder-processes/ProcessFlowchartNodeTypes';
import {GRAY_DARKER} from 'ui-library/Colors';
import SearchInput from 'ui-library/SearchInput';

import ProcessFlowchartBaseNodeRenderer from './ProcessFlowchartBaseNodeRenderer';

interface ProcessFlowchartSidebarProps {
  schemas: Array<{schemaId: string; name: string}>;
  rootSchema: {id: string};
}

const ROUTER_TEMPLATE_NODE: ProcessFlowchartInteractiveNode = {
  name: '',
  instanceData: ROUTER_NODE_INFO.instanceData,
  type: ProcessFlowchartNodeDetailsType.ROUTER_NODE_DETAILS,
  clientId: ROUTER_NODE_INFO.nodeDefinition.dropKey,
  routerConfig: {
    clientId: 'TEMPLATE_ROUTER_CONFIG_CLIENT_ID',
    name: '',
    defaultFunctionSpec: null,
    functionSpecs: [],
  },
};

const END_TEMPLATE_NODE: ProcessFlowchartInteractiveNode = {
  name: '',
  instanceData: END_NODE_INFO.instanceData,
  type: ProcessFlowchartNodeDetailsType.END_NODE_DETAILS,
  clientId: END_NODE_INFO.nodeDefinition.dropKey,
};

function prepareSchemasForSidebar(
  schemas: Array<{schemaId: string; name: string}>,
  rootSchema: {id: string}
): Array<ProcessFlowchartInteractiveNode> {
  return schemas
    .filter(({schemaId}) => schemaId !== rootSchema.id)
    .map(({schemaId, name}) => ({
      name,
      type: ProcessFlowchartNodeDetailsType.TASK_NODE_DETAILS,
      taskSchemaId: schemaId,
      clientId: schemaId,
      width: ProcessFlowchartNodeWidth.SIDEBAR,
      instanceData: TASK_NODE_INFO.instanceData,
      locked: true,
    }));
}

function checkAvailableNodesForDuplicates<TNode extends FlowchartBaseNode>(
  availableNodes: Array<TNode>
): void {
  const availableNodesSet = new Set();
  for (const node of availableNodes) {
    if (!availableNodesSet.has(node.name)) {
      availableNodesSet.add(node.name);
    } else {
      assertAndContinue(false, `Duplicate node with name ${node.name} specified for sidebar`);
    }
  }
}

function getSidebarControlNodesContent(): JSX.Element | null {
  return (
    <>
      <GraphBuilderSidebar.Divider />
      <GraphBuilderSidebar.Heading text="Control nodes" />
      <GraphBuilderSidebar.Divider />

      <div className={css(styles.controlNodesContainer)}>
        <div className={css(styles.controlNodesRow)}>
          <div className={css(styles.controlNodesCol)}>
            <GraphBuilderSidebar.Subheading text="Router" />
          </div>
          <div className={css(styles.controlNodesCol)}>
            <GraphBuilderSidebar.Subheading text="Stop" />
          </div>
        </div>
        <div className={css(styles.controlNodesRow)}>
          <div className={css(styles.controlNodesCol)}>
            <GraphBuilderSidebar.DraggableNode
              dragKey={ROUTER_NODE_INFO.nodeDefinition.dropKey}
              renderNode={({isHighlighted, onDragStart, onDragEnd}) => (
                <ProcessFlowchartBaseNodeRenderer
                  data={{...ROUTER_TEMPLATE_NODE, isHighlighted}}
                  isVisibleInSidebar={true}
                  onDragStart={onDragStart}
                  onDragEnd={onDragEnd}
                />
              )}
            />
          </div>
          <div className={css(styles.controlNodesCol)}>
            <GraphBuilderSidebar.DraggableNode
              dragKey={END_NODE_INFO.nodeDefinition.dropKey}
              renderNode={({isHighlighted, onDragStart, onDragEnd}) => (
                <ProcessFlowchartBaseNodeRenderer
                  data={{...END_TEMPLATE_NODE, isHighlighted}}
                  isVisibleInSidebar={true}
                  onDragStart={onDragStart}
                  onDragEnd={onDragEnd}
                />
              )}
            />
          </div>
        </div>
      </div>
    </>
  );
}

export default function ProcessFlowchartSidebar(props: ProcessFlowchartSidebarProps): JSX.Element {
  const availableSchemas = useMemo(
    () => prepareSchemasForSidebar(props.schemas, props.rootSchema),
    [props.schemas, props.rootSchema]
  );
  const [searchResult, setSearchResult] = useState(availableSchemas);
  checkAvailableNodesForDuplicates(availableSchemas);

  return (
    <GraphBuilderSidebar side={GraphBuilderSidebarSide.RIGHT}>
      {getSidebarControlNodesContent()}
      <div className={css(styles.searchInput)}>
        <SearchInput
          placeholder="Find schemas by name"
          doSearch={(query) => {
            const matchingNodes = availableSchemas.filter(({name}) =>
              name.toLowerCase().includes(query?.toLowerCase() ?? '')
            );
            setSearchResult(matchingNodes);
          }}
          query=""
        />
      </div>
      <div className={css(styles.availableNodesWrapper)}>
        {(availableSchemas.length === 0 && (
          <div className={css(styles.noAvailableNodesWrapper)}>
            No task schemas are available for this Flowchart.
          </div>
        )) ||
          (searchResult.length > 0 &&
            searchResult.map((node) => (
              <React.Fragment key={node.clientId}>
                <GraphBuilderSidebar.DraggableNode
                  dragKey={node.clientId}
                  renderNode={({isHighlighted, onDragStart, onDragEnd}) => (
                    <ProcessFlowchartBaseNodeRenderer
                      data={{...node, isHighlighted}}
                      isVisibleInSidebar={true}
                      onDragStart={onDragStart}
                      onDragEnd={onDragEnd}
                    />
                  )}
                />
                <GraphBuilderSidebar.Spacer />
              </React.Fragment>
            ))) || (
            <div className={css(styles.noAvailableNodesWrapper)}>
              No tasks schemas with that name are available for this Flowchart. Please try a different name.
            </div>
          )}
      </div>
    </GraphBuilderSidebar>
  );
}

const styles = StyleSheet.create({
  searchInput: {
    padding: '8px 10px 10px',
  },
  availableNodesWrapper: {
    maxHeight: 'calc(100% - 196px)',
    width: '100%',
    padding: 10,
    overflowY: 'auto',
    overflowX: 'visible',
  },
  noAvailableNodesWrapper: {
    textAlign: 'center',
    color: GRAY_DARKER,
    margin: '0px 18px',
  },
  controlNodesContainer: {
    width: '100%',
  },
  controlNodesRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  controlNodesCol: {
    flex: '0 0 50%',
    display: 'flex',
    justifyContent: 'center',
    marginLeft: 'auto',
    marginRight: 'auto',
  },
});
